import { createContext, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import { useTranslationApp } from "../lib/i18next";
import {
  startGetAdvertiserLanguage,
  startResetAdvertiserLanguage,
  startSaveAdvertiserLanguage,
  startSetAdvertiserLanguage,
} from "../actions/adsCampaigns";
import { normalizeLanguageCode } from "../utils/catalogs/contentLanguage";

export const AdvertiserContentLanguageContext = createContext(null);

export const AdvertiserContentLanguageProvider = ({ children }) => {
  const dispatch = useDispatchApp();
  const { i18n } = useTranslationApp();
  const role = useSelectorApp((state) => state.auth?.user?.role);
  const advertiserID = useSelectorApp(
    (state) => state.auth?.user?.advertiserID
  );
  const storedLanguage = useSelectorApp(
    (state) => state.user.advertiserLanguage
  );
  const isLoaded = useSelectorApp(
    (state) => state.user.advertiserLanguageLoaded
  );
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!role || !advertiserID) {
      dispatch(startResetAdvertiserLanguage());
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    dispatch(startResetAdvertiserLanguage());

    let cancelled = false;

    const loadLanguage = async () => {
      const result = await dispatch(startGetAdvertiserLanguage());
      if (cancelled || requestId !== requestIdRef.current) {
        return;
      }

      dispatch(startSetAdvertiserLanguage(result?.language));
    };

    void loadLanguage();

    return () => {
      cancelled = true;
    };
  }, [advertiserID, dispatch, role]);

  const onPersistLanguage = useCallback(
    async (language) => {
      if (!language) {
        return { ok: false };
      }

      return dispatch(startSaveAdvertiserLanguage({ language }));
    },
    [dispatch]
  );

  const platformLanguage = normalizeLanguageCode(
    i18n.language || i18n.resolvedLanguage || "en"
  );

  const value = useMemo(
    () => ({
      storedLanguage,
      isLoaded,
      platformLanguage,
      onPersistLanguage,
    }),
    [isLoaded, onPersistLanguage, platformLanguage, storedLanguage]
  );

  return (
    <AdvertiserContentLanguageContext.Provider value={value}>
      {children}
    </AdvertiserContentLanguageContext.Provider>
  );
};
